import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { scrubSentryBreadcrumb, scrubSentryEvent } from '@/shared/lib/sentry-scrub';
import { mayReloadForChunk } from '@/shared/lib/chunk-reload';

import { queryClient } from '@/shared/lib/query-client';
import { ToastProvider } from '@/shared/components/Toast';
import { PlanRequiredListener } from '@/shared/components/PlanRequiredListener';
import { ConfirmDialogHost } from '@/shared/components/ConfirmDialog';
import { App } from './App';

// Initialize i18n (side-effect import)
import '@/shared/i18n/config';

// Global styles
import './globals.css';

// A new deploy content-hashes the chunk filenames; a tab still holding the old
// index.html fails to fetch a lazy route's (now-missing) chunk. Vite fires
// `vite:preloadError` for exactly this — reload once to pull the fresh build so
// the user never hits the error screen. The session flag guards against a loop.
window.addEventListener('vite:preloadError', () => {
  if (mayReloadForChunk()) window.location.reload();
});

// Initialize Sentry error tracking (production only)
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
    // No replay integration is installed, so no replay options: they did nothing.
    sendDefaultPii: false,
    beforeSend: (event) => scrubSentryEvent(event),
    beforeBreadcrumb: (crumb) => scrubSentryBreadcrumb(crumb),
  });
}

const root = document.getElementById('root');

if (!root) {
  throw new Error('Root element not found. Ensure index.html contains <div id="root"></div>.');
}

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastProvider>
          <PlanRequiredListener />
          <App />
          <ConfirmDialogHost />
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);

// If the app has stayed up for a bit, clear the one-shot reload guard so a LATER
// deploy's stale-chunk can auto-recover too. A genuine reload loop fails within
// seconds (before this fires), so the guard stays set and the loop is broken.
setTimeout(() => {
  try {
    sessionStorage.removeItem('ub-chunk-reloaded');
  } catch {
    /* sessionStorage unavailable — nothing to clear */
  }
}, 10_000);
